import { createBrowserRouter } from 'react-router'
import { AuthCallbackPage } from '@/features/auth'
import { DashboardPage } from '@/features/dashboard'
import { RootPage } from '@/features/landing'
import { PrecadragePage, NewProjectPage } from '@/features/precadrage'
import { PricingPage } from '@/features/pricing'
import {
  ScopingV2Page,
  PostScopingPage,
  ScopingPreviewPage,
} from '@/features/scoping-v2'
import {
  ProjectKanbanPage,
  ProjectEditPage,
  FeatureDetailPage,
  UserStoryDetailPage,
  TeamExchangePage,
  SpecsPrintPage,
} from '@/features/team'
import { SettingsPage } from '@/features/settings'
import { PaymentSuccessPage, PaymentCancelPage } from '@/features/payment'
import {
  ProtectedRoute,
  LanguageLayout,
  LanguageRedirect,
  LegacyRedirect,
} from '@/shared/components'

export const router = createBrowserRouter([
  // Root: Redirect to /:lang based on browser detection
  {
    path: '/',
    element: <LanguageRedirect />,
  },

  // All routes with language prefix
  {
    path: '/:lang',
    element: <LanguageLayout />,
    children: [
      // Landing page (index of /:lang)
      {
        index: true,
        element: <RootPage />,
      },

      // Public routes
      {
        path: 'pricing',
        element: <PricingPage />,
      },
      {
        path: 'precadrage',
        element: <PrecadragePage />,
      },

      // Auth callback (special case - no guard)
      {
        path: 'auth/callback',
        element: <AuthCallbackPage />,
      },

      // Protected routes (redirect to landing if not authenticated)
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: 'dashboard',
            element: <DashboardPage />,
          },
          {
            path: 'projects/new',
            element: <NewProjectPage />,
          },
          // Short URLs - flat structure with short_id
          {
            path: 'p/:shortId',
            element: <ProjectKanbanPage />,
          },
          {
            path: 'p/:shortId/edit',
            element: <ProjectEditPage />,
          },
          {
            path: 'p/:shortId/specs/print',
            element: <SpecsPrintPage />,
          },
          // Scoping V2 routes (use short_id for consistent URL pattern)
          {
            path: 'scoping/:shortId',
            element: <ScopingV2Page />,
          },
          {
            path: 'scoping/:shortId/complete',
            element: <PostScopingPage />,
          },
          {
            path: 'scoping/:shortId/preview',
            element: <ScopingPreviewPage />,
          },
          {
            path: 'f/:shortId',
            element: <FeatureDetailPage />,
          },
          {
            path: 'us/:shortId',
            element: <UserStoryDetailPage />,
          },
          // Team exchange zone
          {
            path: 't/:shortId/exchange',
            element: <TeamExchangePage />,
          },
          // Settings
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            path: 'settings/:tab',
            element: <SettingsPage />,
          },
          // Payment
          {
            path: 'payment/success',
            element: <PaymentSuccessPage />,
          },
          {
            path: 'payment/cancel',
            element: <PaymentCancelPage />,
          },
        ],
      },
    ],
  },

  // Legacy routes (without language prefix) - redirect to /en/...
  // These handle bookmarked/shared URLs from before i18n implementation
  {
    path: '/dashboard',
    element: <LegacyRedirect />,
  },
  {
    path: '/pricing',
    element: <LegacyRedirect />,
  },
  {
    path: '/precadrage',
    element: <LegacyRedirect />,
  },
  {
    path: '/projects/new',
    element: <LegacyRedirect />,
  },
  {
    path: '/p/:shortId',
    element: <LegacyRedirect />,
  },
  {
    path: '/p/:shortId/edit',
    element: <LegacyRedirect />,
  },
  {
    path: '/p/:shortId/specs/print',
    element: <LegacyRedirect />,
  },
  {
    path: '/scoping/:shortId',
    element: <LegacyRedirect />,
  },
  {
    path: '/scoping/:shortId/complete',
    element: <LegacyRedirect />,
  },
  {
    path: '/scoping/:shortId/preview',
    element: <LegacyRedirect />,
  },
  {
    path: '/f/:shortId',
    element: <LegacyRedirect />,
  },
  {
    path: '/us/:shortId',
    element: <LegacyRedirect />,
  },
  {
    path: '/t/:shortId/exchange',
    element: <LegacyRedirect />,
  },
  {
    path: '/settings',
    element: <LegacyRedirect />,
  },
  {
    path: '/settings/:tab',
    element: <LegacyRedirect />,
  },
  {
    path: '/payment/success',
    element: <LegacyRedirect />,
  },
  {
    path: '/payment/cancel',
    element: <LegacyRedirect />,
  },
  {
    path: '/auth/callback',
    element: <LegacyRedirect />,
  },
])
